import React from 'react';

import { ChevronDownIcon } from '../../assets';
import TrackBar from './TrackBar';

/**
 * One chapter of "تحليل الوحدات الدراسية", open or closed — Figma nodes
 * 16370:191360 (open) and 16370:191433 (closed).
 *
 * Not `ChapterPanel`, though it is the same idea: the course page's header is
 * white and keeps its chevron beside the title, where this frame's is slate/50
 * and gathers the chevron, the bar and the percentage together at the far end,
 * away from the title. Growing `ChapterPanel` a prop for each difference would
 * leave neither design easy to read.
 *
 * The bar and the percentage are green/350 when the chapter reaches the exam's
 * pass line and red/400 when it does not (`tone`), as the frame's 75% and 35%
 * are drawn.
 *
 * ORDER
 *
 * Title first, then chevron, bar, percentage: in Arabic the title sits at the
 * right and the percentage ends the row at the far left, where the design puts
 * it. The whole header is one <button> with `aria-expanded`, inside the `h3`
 * that names the chapter. The chevron points down while closed and up while
 * open, as on the course page.
 */
const TONES = {
    pass: { bar: 'done', text: 'tw-text-v2-student-done' },
    fail: { bar: 'alert', text: 'tw-text-v2-student-dot' },
};

const UnitPanel = ({ id, title, percent, tone = 'pass', isOpen = false, onToggle, children }) => {
    const colours = TONES[tone] ?? TONES.pass;

    return (
        <div className="tw-overflow-hidden tw-rounded-[16px] tw-border tw-border-solid tw-border-v2-ground-400 tw-bg-white">
            <h3 className="tw-m-0">
                <button
                    type="button"
                    id={`${id}-header`}
                    aria-expanded={isOpen}
                    aria-controls={`${id}-body`}
                    onClick={onToggle}
                    className={
                        'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-[16px] ' +
                        'tw-border-0 tw-bg-v2-ground-100 tw-p-[16px] tw-text-start sm:tw-p-[24px] ' +
                        'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-v2-brand ' +
                        (isOpen ? 'tw-border-b tw-border-solid tw-border-v2-ground-400' : '')
                    }
                >
                    <span className="tw-min-w-0 tw-text-[16px] tw-font-bold tw-leading-[1.5] tw-text-v2-student-name">
                        {title}
                    </span>

                    <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-[12px]">
                        <ChevronDownIcon
                            width={16}
                            height={16}
                            aria-hidden
                            className={`tw-shrink-0 tw-text-v2-student-nav tw-transition-transform ${isOpen ? 'tw-rotate-180' : ''}`}
                        />
                        <TrackBar value={percent} tone={colours.bar} className="tw-hidden tw-w-[120px] sm:tw-block" />
                        <span className={`tw-whitespace-nowrap tw-text-[14px] tw-font-bold tw-leading-[1.6] ${colours.text}`}>
                            {percent}%
                        </span>
                    </span>
                </button>
            </h3>

            {isOpen && (
                <div id={`${id}-body`} role="region" aria-labelledby={`${id}-header`}>
                    {children}
                </div>
            )}
        </div>
    );
};

export default UnitPanel;
